import { ReactNode, useEffect } from 'react';
import { useSnapshot } from 'valtio';

import * as checkoutStore from '@cartwhl/common/stores/checkoutStore';
import * as updatedProductStore from '@cartwhl/common/stores/updatedProductStore';
import Image from '@cartwhl/ui/atoms/Image';
import Loading from '@cartwhl/ui/atoms/Loading';
import Text from '@cartwhl/ui/base/Text';
import ChevronRightIcon from '@cartwhl/ui/icons/ChevronRightIcon';
import Button from '@cartwhl/ui/input/Button';
import Modal from '@cartwhl/ui/molecules/Modal';

type Slide = 1;

export default function ReadyForMoreSpinsModal({
  close,
}: {
  close: () => void;
}) {
  const slide = 1 as Slide;

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      close();
    }
  };
  const slides = {
    1: {
      title: "You're ready for more Spins!",
      component: (
        <Slide
          close={close}
          text={
            <>
              Nice work, you're ready for a Cartwheel plan. We recently
              simplified our plans and "Spins" are going away as part of that —
              choose Individual Basic or Individual Pro for monthly credits and
              new workspace features. The new plans are affordable and offer
              more value.{' '}
              <a
                href="https://getcartwheel.com/pricing"
                target="_blank"
                rel="noreferrer"
                className="text-blue underline"
              >
                Learn more at getcartwheel.com/pricing
              </a>
              .
            </>
          }
        />
      ),
    },
  };

  return (
    <Modal
      title={
        <Text as="h3" variant="studio/body/medium/md" className="font-display">
          {slides[slide].title}
        </Text>
      }
      open={true}
      onOpenChange={handleOpenChange}
      closeOnClickOutside={false}
    >
      {slides[slide].component}
    </Modal>
  );
}

function Slide({ text, close }: { text: ReactNode; close: () => void }) {
  const closeMe = () => {
    close();
  };

  const { products } = useSnapshot(updatedProductStore.state);
  const quantity = 1;

  const basicProduct = products.individual_basic;
  const proProduct = products.individual_pro;
  const basicPrice =
    basicProduct &&
    updatedProductStore.getPriceByProductAndMode(basicProduct.id, 'month');
  const proPrice =
    proProduct &&
    updatedProductStore.getPriceByProductAndMode(proProduct.id, 'month');

  useEffect(() => {
    updatedProductStore.fetchProducts();
  }, []);

  const handleBasicCheckout = async () => {
    const hostedCheckoutURL = await checkoutStore.completeHostedCheckout(
      basicPrice ?? null,
      quantity,
    );
    if (hostedCheckoutURL) {
      window.location.href = hostedCheckoutURL;
    }
  };

  const handleProCheckout = async () => {
    const hostedCheckoutURL = await checkoutStore.completeHostedCheckout(
      proPrice ?? null,
      quantity,
    );
    if (hostedCheckoutURL) {
      window.location.href = hostedCheckoutURL;
    }
  };

  return (
    <div>
      <Image
        src={
          'https://resources.getcartwheel.com/share/images/modals/more_spins.webp'
        }
        alt={`Cartwheel characters celebrating.`}
        className="bg-gray-15 mb-4 block aspect-452/254 w-full rounded-2xl"
      />
      <Text as="p" variant="studio/body/sm" className="font-normal">
        {text}
      </Text>
      <div className="mt-6 flex gap-4">
        <button
          type="button"
          onClick={handleBasicCheckout}
          disabled={!basicPrice}
          className="bg-gray-15 hover:bg-gray-30 dark:bg-gray-75 dark:hover:bg-gray-60 relative flex flex-1 flex-col items-start justify-between rounded-xl p-6 transition-colors disabled:pointer-events-none disabled:opacity-50"
        >
          <Text as="span" variant="studio/title/md">
            Individual Basic
          </Text>
          <Text as="span" variant="studio/body/sm" className="mt-1 text-left">
            200 credits / month
          </Text>
          <MonthlyPriceFrom cents={basicPrice?.unit_amount ?? null} />
          <span className="absolute top-1/2 right-4 -translate-y-1/2 transform text-xl">
            <ChevronRightIcon />
          </span>
        </button>
        <button
          type="button"
          onClick={handleProCheckout}
          disabled={!proPrice}
          className="bg-blue/10 border-blue hover:bg-blue/20 dark:hover:bg-blue/30 relative flex flex-1 flex-col items-start justify-between rounded-xl border p-6 transition-colors disabled:pointer-events-none disabled:opacity-50"
        >
          <Text as="span" variant="studio/title/md">
            Individual Pro
          </Text>
          <Text as="span" variant="studio/body/sm" className="mt-1 text-left">
            500 credits / month
          </Text>
          <MonthlyPriceFrom cents={proPrice?.unit_amount ?? null} />
          <span className="absolute top-1/2 right-4 -translate-y-1/2 transform text-xl">
            <ChevronRightIcon />
          </span>
        </button>
      </div>
      <div className="mt-6 flex justify-start">
        <Button onClick={closeMe}>Close</Button>
      </div>
    </div>
  );
}

function MonthlyPriceFrom({ cents }: { cents: number | null }) {
  return (
    <div className="mt-4 flex items-baseline">
      <Text as="span" variant="studio/body/sm">
        From
      </Text>
      <Text
        as="span"
        variant="studio/body/sm"
        className="ml-1 inline-flex items-center gap-0.5 font-medium"
      >
        {cents != null ? (
          `$${updatedProductStore.formatMoney(cents).toFixed(0)}/mo`
        ) : (
          <>
            $
            <Loading className="h-3.5 w-3.5 shrink-0" strokeWidth={3} />
            /mo
          </>
        )}
      </Text>
    </div>
  );
}
